import {
  type ExamWorksheetData,
  isUsWorksheet,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  MSK_REGION_CONFIG,
  scannedMskRegions,
} from "../worksheets/msk-regions";
import { usDefinitionFor } from "../worksheets/us/us-definitions";
import { CPT_DESCRIPTIONS } from "./cpt-descriptions";
import {
  type CptSuggestionResult,
  suggestCptCodes,
  suggestMskRegion,
} from "./epocus-billing-engine";

export type BillingLine = {
  key: string;
  cpt: string;
  label: string;
  sideModifier: string | null;
  units: number;
  preTicked: boolean;
  notes: string[];
  modifier?: string | null;
};

const lineKey = (
  cpt: string,
  sideModifier: string | null,
  label: string,
): string => [cpt, sideModifier ?? "", label].join("|");

const suggestionLine =
  (preTicked: boolean) =>
  ({
    procedureCode,
    description,
    payerRiskNote,
    completenessWarning,
  }: CptSuggestionResult): BillingLine => {
    const sideModifier = procedureCode.sideModifier ?? null;
    return {
      key: lineKey(procedureCode.cpt, sideModifier, description),
      cpt: procedureCode.cpt,
      label: description,
      sideModifier,
      units: procedureCode.units,
      preTicked,
      notes: [payerRiskNote, completenessWarning].filter(
        (note): note is string => !!note,
      ),
    };
  };

export const billingLinesFor = (data: ExamWorksheetData): BillingLine[] => {
  if (isUsWorksheet(data)) {
    return (usDefinitionFor(data.examType)?.billing.codes ?? []).map(
      (code) => ({
        key: code.cpt,
        cpt: code.cpt,
        label: code.label,
        sideModifier: null,
        units: 1,
        preTicked: code.preTick === true,
        notes: [],
      }),
    );
  }

  if (data.examType === "msk") {
    const regions = scannedMskRegions(data);
    if (regions.length > 0) {
      return regions.flatMap((region) =>
        suggestMskRegion(data, region, null).map(
          suggestionLine(
            MSK_REGION_CONFIG[region].billing.codes.some(
              (code) => code.preTick === true,
            ),
          ),
        ),
      );
    }
  }

  return suggestCptCodes(data).map(suggestionLine(true));
};

export const otherBillingLines = (
  lines: readonly BillingLine[],
): BillingLine[] =>
  Object.entries(CPT_DESCRIPTIONS)
    .filter(([cpt]) => !lines.some((line) => line.cpt === cpt))
    .map(([cpt, label]) => ({
      key: cpt,
      cpt,
      label,
      sideModifier: null,
      units: 1,
      preTicked: false,
      notes: [],
    }));
